// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { SchemaModel } from '../models/Schema'
import type { VirtualCollectionAdapter } from './VirtualCollectionAdapter'
import type {
  KeyRing,
  EncryptionContext,
  KeyLookupFn,
  FieldEncryptionConfig,
} from '@epicdm/flowstate-crypto'
import { encryptFields, decryptFields } from '@epicdm/flowstate-crypto'

/**
 * Parse the encryption config from a schema record.
 * Returns an empty array if no encrypted fields are configured.
 *
 * Handles both pre-parsed arrays and JSON-string storage formats,
 * since RxDB may return the value as a plain object array while
 * REST responses may return it as a JSON string.
 */
function parseEncryptionConfig(schema: SchemaModel): FieldEncryptionConfig[] {
  const raw = (schema as unknown as Record<string, unknown>).encryptedFields
  if (!raw) return []
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return Array.isArray(parsed) ? (parsed as FieldEncryptionConfig[]) : []
  } catch {
    return []
  }
}

/**
 * Decorator that adds transparent field-level encryption to a {@link VirtualCollectionAdapter}.
 *
 * - On write (insert/update/upsert): encrypts configured fields before passing to VCA
 * - On read (find/findOne): decrypts encrypted field envelopes after VCA returns
 * - When KeyRing is locked: reads return `'[encrypted]'` placeholders, writes throw
 *
 * The adapter does NOT mutate the VCA — it wraps it as a pure decorator.
 *
 * @example
 * ```typescript
 * const vca = new VirtualCollectionAdapter(records, schema)
 * const adapter = new EncryptedCollectionAdapter(vca, schema, keyRing, context)
 * const doc = await adapter.insert({ name: 'Alice', ssn: '123-45-6789' })
 * // ssn is stored encrypted in VCA, returned decrypted to caller
 * ```
 */
export class EncryptedCollectionAdapter {
  private readonly encryptionConfig: FieldEncryptionConfig[]

  /**
   * @param vca - The VirtualCollectionAdapter to wrap
   * @param schema - Schema definition containing encrypted field configuration
   * @param keyRing - The KeyRing used for encrypt/decrypt operations
   * @param context - Encryption context (orgId, workspaceId, userId)
   * @param lookupKey - Optional key lookup function for group key resolution
   */
  constructor(
    private readonly vca: VirtualCollectionAdapter,
    schema: SchemaModel,
    private readonly keyRing: KeyRing,
    private readonly context: EncryptionContext,
    private readonly lookupKey?: KeyLookupFn
  ) {
    this.encryptionConfig = parseEncryptionConfig(schema)
  }

  /** Delegates to the wrapped VCA's collection name. */
  get name(): string {
    return this.vca.name
  }

  /** Delegates to the wrapped VCA's schema ID. */
  get schemaId(): string {
    return this.vca.schemaId
  }

  /**
   * Find records matching the given selector.
   * Encrypted fields in returned documents are decrypted transparently.
   * Returns `'[encrypted]'` placeholder strings when the KeyRing is locked.
   */
  async find(
    options: {
      selector?: Record<string, unknown>
      limit?: number
      skip?: number
      sort?: Array<Record<string, string>>
    } = {}
  ): Promise<Record<string, unknown>[]> {
    const results = await this.vca.find(options)
    return Promise.all(results.map(doc => decryptFields(doc, this.keyRing)))
  }

  /**
   * Find a single record by ID.
   * Encrypted fields in the returned document are decrypted transparently.
   * Returns `null` if no record is found.
   */
  async findOne(id: string): Promise<Record<string, unknown> | null> {
    const doc = await this.vca.findOne(id)
    if (!doc) return null
    return decryptFields(doc, this.keyRing)
  }

  /**
   * Insert a new record, encrypting configured fields before storage.
   *
   * @throws When the KeyRing is locked and there are fields to encrypt
   */
  async insert(flatDoc: Record<string, unknown>): Promise<Record<string, unknown>> {
    const encrypted = await encryptFields(
      flatDoc,
      this.encryptionConfig,
      this.keyRing,
      this.context,
      this.lookupKey
    )
    const result = await this.vca.insert(encrypted)
    return decryptFields(result, this.keyRing)
  }

  /**
   * Update an existing record by ID, encrypting any configured fields in the patch.
   * Returns the updated document with encrypted fields decrypted, or `null` if not found.
   *
   * @throws When the KeyRing is locked and the patch contains fields to encrypt
   */
  async update(
    id: string,
    updates: Record<string, unknown>
  ): Promise<Record<string, unknown> | null> {
    const encrypted = await encryptFields(
      updates,
      this.encryptionConfig,
      this.keyRing,
      this.context,
      this.lookupKey
    )
    const result = await this.vca.update(id, encrypted)
    if (!result) return null
    return decryptFields(result, this.keyRing)
  }

  /**
   * Remove a record by ID. Delegates directly to VCA — no encryption needed for deletes.
   * Returns `true` if the record was found and removed, `false` otherwise.
   */
  async remove(id: string): Promise<boolean> {
    return this.vca.remove(id)
  }

  /**
   * Upsert a record, encrypting configured fields before storage.
   * Inserts if no record with the given ID exists, updates otherwise.
   *
   * @throws When the KeyRing is locked and there are fields to encrypt
   */
  async upsert(flatDoc: Record<string, unknown>): Promise<Record<string, unknown>> {
    const encrypted = await encryptFields(
      flatDoc,
      this.encryptionConfig,
      this.keyRing,
      this.context,
      this.lookupKey
    )
    const result = await this.vca.upsert(encrypted)
    return decryptFields(result, this.keyRing)
  }
}

/**
 * Derive the encryptedFields config from a schema's field definitions.
 * Called by schema save handlers to keep the encryptedFields column in sync.
 *
 * @param fields - Array of field definitions from the schema
 * @returns Array of encrypted field configurations containing fieldName and encryptionScope
 *
 * @example
 * ```typescript
 * const encryptedFields = deriveEncryptedFieldsConfig(schema.fields)
 * // [{ fieldName: 'ssn', encryptionScope: 'owner' }]
 * ```
 */
export function deriveEncryptedFieldsConfig(
  fields: Array<{ name: string; config?: { encrypted?: boolean; encryptionScope?: string } }>
): Array<{ fieldName: string; encryptionScope: string }> {
  return fields
    .filter(f => f.config?.encrypted)
    .map(f => ({
      fieldName: f.name,
      encryptionScope: f.config?.encryptionScope ?? 'owner',
    }))
}
